import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateTime, searchMatch } from '@/lib/utils';
import { Activity, Mail, ExternalLink, RefreshCw, User, XCircle, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminLogsTab = ({
    logs = [],
    logFilters,
    setLogFilters,
    logUsers = [],
    logIps = [],
    logActions = [],
    onRefresh
}) => {
    const logsList = Array.isArray(logs) ? logs : [];
    const usersList = Array.isArray(logUsers) ? logUsers : [];
    const ipsList = Array.isArray(logIps) ? logIps : [];
    const actionsList = Array.isArray(logActions) ? logActions : [];
    
    // Local search states for real-time filtering
    const [userSearch, setUserSearch] = useState('');
    const [ipSearch, setIpSearch] = useState('');
    
    // Filter users and IPs based on search (for dropdown)
    const filteredUsersList = useMemo(() => {
        if (!userSearch) return usersList;
        const search = userSearch.toLowerCase();
        return usersList.filter(u => 
            u.username?.toLowerCase().includes(search) || 
            u.email?.toLowerCase().includes(search)
        );
    }, [usersList, userSearch]);
    
    const filteredIpsList = useMemo(() => {
        if (!ipSearch) return ipsList;
        return ipsList.filter(ip => ip?.includes(ipSearch));
    }, [ipsList, ipSearch]);

    // Filter logs based on search inputs (real-time filtering with diacritics support)
    const filteredLogs = useMemo(() => {
        let filtered = logsList;
        
        // Filter by user search text (with diacritics normalization)
        if (userSearch) {
            filtered = filtered.filter(log => 
                searchMatch(log.username, userSearch) || 
                searchMatch(log.email, userSearch)
            );
        }
        
        // Filter by IP search text
        if (ipSearch) {
            filtered = filtered.filter(log => 
                log.ip_address?.includes(ipSearch)
            );
        }
        
        return filtered;
    }, [logsList, userSearch, ipSearch]);
    
    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Jurnal Evenimente</CardTitle>
                    <CardDescription>
                        Activități recente pe platformă cu detalii despre utilizator și dispozitiv
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 p-3 bg-muted/30 rounded-lg">
                        <div>
                            <Label className="text-xs mb-1 block">Caută după Utilizator/Email</Label>
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Caută username sau email..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    className="h-9 pl-8"
                                    data-testid="log-user-search"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Caută după IP</Label>
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    placeholder="Caută adresă IP..."
                                    value={ipSearch}
                                    onChange={(e) => setIpSearch(e.target.value)}
                                    className="h-9 pl-8"
                                    data-testid="log-ip-search"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs mb-1 block">Filtrează după Acțiune</Label>
                            <Select
                                value={logFilters.action}
                                onValueChange={(v) => setLogFilters(prev => ({ ...prev, action: v === 'all' ? '' : v }))}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Toate acțiunile" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px] overflow-y-auto">
                                    <SelectItem value="all">Toate acțiunile</SelectItem>
                                    {actionsList.map(a => (
                                        <SelectItem key={a} value={a}>{a}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => {
                                    setLogFilters({ username: '', ip: '', action: '' });
                                    setUserSearch('');
                                    setIpSearch('');
                                    onRefresh();
                                }}
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Resetează
                            </Button>
                        </div>
                    </div>

                    {/* Active filters indicator */}
                    {(userSearch || ipSearch || logFilters.action) && (
                        <div className="flex gap-2 mb-4 flex-wrap">
                            {userSearch && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    Căutare: "{userSearch}"
                                    <XCircle className="w-3 h-3 cursor-pointer" onClick={() => setUserSearch('')} />
                                </Badge>
                            )}
                            {ipSearch && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                    IP: "{ipSearch}"
                                    <XCircle className="w-3 h-3 cursor-pointer" onClick={() => setIpSearch('')} />
                                </Badge>
                            )}
                            {logFilters.action && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                    {logFilters.action}
                                    <XCircle className="w-3 h-3 cursor-pointer" onClick={() => setLogFilters(prev => ({ ...prev, action: '' }))} />
                                </Badge>
                            )}
                        </div>
                    )}

                    {/* Logs count */}
                    <p className="text-sm text-muted-foreground mb-3">
                        {filteredLogs.length} evenimente găsite {(userSearch || ipSearch) && `(din ${logsList.length} total)`}
                    </p>
                </CardContent>
                <CardContent className="p-0 max-h-[500px] overflow-y-auto border-t">
                    <div className="divide-y">
                        {filteredLogs.map((log, i) => (
                            <div key={i} className="p-4 hover:bg-muted/30 transition-colors">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                            <Activity className="w-4 h-4 text-primary" />
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <button
                                                    className="font-semibold text-foreground hover:text-primary hover:underline"
                                                    onClick={() => setUserSearch(log.username)}
                                                    title={log.username}
                                                >
                                                    {log.name || log.username || 'Unknown'}
                                                </button>
                                                <Badge variant="outline" className="text-xs cursor-pointer hover:bg-primary/10"
                                                    onClick={() => setLogFilters(prev => ({ ...prev, action: log.action }))}>
                                                    {log.action}
                                                </Badge>
                                            </div>
                                            {log.email && (
                                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    {log.email}
                                                </p>
                                            )}
                                            {log.details && Object.keys(log.details).length > 0 && (
                                                <div className="text-sm text-muted-foreground">
                                                    {Object.entries(log.details)
                                                        .filter(([k]) => !['ip_address', 'listing_id', 'slug', 'target_user', 'target_username'].includes(k))
                                                        .map(([k, v]) => {
                                                            // Show target_user_name instead of target_user
                                                            if (k === 'target_user_name') {
                                                                return `utilizator țintă: ${v}`;
                                                            }
                                                            return `${k}: ${v}`;
                                                        })
                                                        .join(' • ')}
                                                    {log.action === 'create_listing' && log.details?.slug && (
                                                        <a
                                                            href={`/listing/${log.details.slug}`}
                                                            className="ml-2 text-primary hover:underline inline-flex items-center gap-1"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <ExternalLink className="w-3 h-3" />
                                                            Vezi piesa
                                                        </a>
                                                    )}
                                                    {log.action === 'create_listing' && log.details?.listing_id && !log.details?.slug && (
                                                        <a
                                                            href={`/listing/${log.details.slug || log.details.listing_id}`}
                                                            className="ml-2 text-primary hover:underline inline-flex items-center gap-1"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <ExternalLink className="w-3 h-3" />
                                                            Vezi piesa
                                                        </a>
                                                    )}
                                                    {log.action === 'place_bid' && log.details?.listing_id && (
                                                        <Link
                                                            to={`/listing/${log.details.slug || log.details.listing_id}`}
                                                            className="ml-2 text-primary hover:underline inline-flex items-center gap-1"
                                                        >
                                                            <ExternalLink className="w-3 h-3" />
                                                            Vezi piesa
                                                        </Link>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right text-xs space-y-1 shrink-0">
                                        <p className="text-muted-foreground">{formatDateTime(log.created_at)}</p>
                                    </div>
                                </div>

                                {/* Device & Location Info */}
                                <div className="mt-3 ml-11 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    {log.ip_address && (
                                        <button
                                            className="flex items-center gap-1 hover:text-primary hover:underline"
                                            onClick={() => setIpSearch(log.ip_address)}
                                        >
                                            <span className="font-medium">IP:</span> {log.ip_address}
                                        </button>
                                    )}
                                    {log.browser && log.browser !== 'Unknown' && (
                                        <span className="flex items-center gap-1">
                                            <span className="font-medium">Browser:</span> {log.browser}
                                        </span>
                                    )}
                                    {log.os && log.os !== 'Unknown' && (
                                        <span className="flex items-center gap-1">
                                            <span className="font-medium">OS:</span> {log.os}
                                        </span>
                                    )}
                                    {log.is_mobile && (
                                        <Badge variant="secondary" className="text-xs">Mobile</Badge>
                                    )}
                                </div>

                                {/* Preview Card for place_bid and create_listing */}
                                {log.preview && (
                                    <div className="mt-3 ml-11 p-3 bg-muted/30 rounded-lg border">
                                        <div className="flex items-center gap-3">
                                            {log.preview.image && (
                                                <img
                                                    src={log.preview.image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${log.preview.image}` : log.preview.image}
                                                    alt={log.preview.title}
                                                    className="w-12 h-12 rounded-lg object-cover shrink-0"
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{log.preview.title}</p>
                                                {log.preview.type === 'bid' && (
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <span>Ofertă: <strong className="text-primary">{log.preview.bid_amount} RON</strong></span>
                                                        <span>•</span>
                                                        <span>Preț curent: {log.preview.current_price} RON</span>
                                                    </div>
                                                )}
                                                {log.preview.type === 'listing' && (
                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <Badge variant="outline" className="text-xs">
                                                            {log.preview.listing_type === 'auction' ? 'Licitație' : 'Vânzare'}
                                                        </Badge>
                                                        <span>{log.preview.price} RON</span>
                                                        {log.preview.category && <span>• {log.preview.category}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <a
                                                href={`/listing/${log.preview.slug || log.details?.listing_id}`}
                                                className="text-primary hover:underline text-xs flex items-center gap-1"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Vezi
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {/* Full User Agent - collapsible */}
                                {log.user_agent && log.user_agent !== 'unknown' && (
                                    <details className="mt-2 ml-11">
                                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                                            Vezi User Agent complet
                                        </summary>
                                        <p className="mt-1 text-xs text-muted-foreground font-mono bg-muted/50 p-2 rounded break-all">
                                            {log.user_agent}
                                        </p>
                                    </details>
                                )}
                            </div>
                        ))}
                        {filteredLogs.length === 0 && (
                            <div className="p-8 text-center text-muted-foreground">
                                <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Nu s-au găsit evenimente pentru criteriile selectate</p>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminLogsTab;
